import { CaretLeft, CaretRight, Copy, DownloadSimple } from '@phosphor-icons/react';
import type {
  FeatureState,
  LimitUsage,
  PlanLimits,
  PlatformAuditEntry,
  PlatformTotpEnrollment,
  TenantStatus,
} from '@sm/api';
import { PLAN_LIMIT_KEYS } from '@sm/core';
import type { TFunction } from 'i18next';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { copyText } from '@/components/ui/States';
import { cx, toneClass } from '@/components/ui/cx';
import { fmtDec, fmtInt } from '@/lib/format';
import { LIMIT_TONE_BAR, limitView, TENANT_STATUS_TONE } from '@/lib/plan';

import { downloadBackupCodesPdf } from './backupCodesPdf';

export function TenantStatusBadge({ status, size }: { status: TenantStatus; size?: 'sm' | 'md' }) {
  const { t } = useTranslation();
  return (
    <Badge tone={TENANT_STATUS_TONE[status]} {...(size ? { size } : {})}>
      {t(`common:tenantStatus.${status}`)}
    </Badge>
  );
}

/** Zaglavlje stranice platforme (naslov + podnaslov + akcije), postavlja i naslov taba. */
export function PlatformPageHeader({
  title,
  subtitle,
  actions,
  crumb,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  crumb?: ReactNode;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    document.title = t('panel:platform.pageTitle', { page: title });
  }, [title, t]);
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="flex min-w-0 flex-col gap-1">
        {crumb ? <div className="text-[13px] font-medium text-muted">{crumb}</div> : null}
        <h1 className="m-0 font-heading text-[28px] leading-[1.15] font-semibold tracking-[-.02em]">
          {title}
        </h1>
        {subtitle ? <p className="m-0 text-sm text-muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (p: number) => void;
}) {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  return (
    <nav
      aria-label={t('panel:platform.pagination.label')}
      className="flex items-center justify-end gap-2 text-[13px] text-muted"
      data-testid="platform-pagination"
    >
      <span>{t('panel:platform.pagination.page', { page, pages, total })}</span>
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        aria-label={t('panel:platform.pagination.prev')}
        className="grid h-8 w-8 place-items-center rounded-md border border-line2 text-text disabled:opacity-40"
      >
        <CaretLeft size={14} />
      </button>
      <button
        type="button"
        disabled={page >= pages}
        onClick={() => onPage(page + 1)}
        aria-label={t('panel:platform.pagination.next')}
        className="grid h-8 w-8 place-items-center rounded-md border border-line2 text-text disabled:opacity-40"
      >
        <CaretRight size={14} />
      </button>
    </nav>
  );
}

/** Trake iskorištenosti limita (efektivni limiti firme + upotreba). */
export function LimitBars({
  limits,
  usage,
  testId = 'platform-limit',
}: {
  limits: PlanLimits;
  usage: LimitUsage;
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2.5">
      {PLAN_LIMIT_KEYS.map((key) => {
        const v = limitView(key, limits, usage);
        const f = (n: number) => (key === 'storageGb' ? fmtDec(n) : fmtInt(n));
        return (
          <div
            key={key}
            className="flex flex-col gap-1"
            data-testid={testId}
            data-limit={key}
            data-tone={v.tone}
          >
            <div className="flex items-baseline justify-between gap-2 text-[13px]">
              <span className="text-text2">{t(`common:planLimit.${key}`)}</span>
              <span className="font-semibold tabular-nums">
                {v.limit == null
                  ? t('panel:settings.plan.usageUnlimited', { used: f(v.used) })
                  : t('panel:settings.plan.usage', { used: f(v.used), limit: f(v.limit) })}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-bg3">
              {v.limit == null ? null : (
                <div
                  className={cx('h-full rounded-full', LIMIT_TONE_BAR[v.tone])}
                  style={{ width: `${Math.max(v.pct, v.used > 0 ? 3 : 0)}%` }}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export const FEATURE_TONE: Record<FeatureState, string> = {
  on: toneClass.ok,
  preview: toneClass.caution,
  off: toneClass.neutral,
};

/** Tekst audit zapisa platforme: `panel:platform.audit.<action>` (nepoznata akcija ostaje ključ). */
export function auditText(t: TFunction, e: PlatformAuditEntry): string {
  const key = `panel:platform.audit.${e.action}`;
  return t(key, {
    defaultValue: e.action,
    tenant: e.tenantName ?? '—',
    user: e.targetUserName ?? '—',
    actor: e.actorName ?? '—',
  });
}

/** Sekcija kartice na detalju firme. */
export function Section({
  title,
  actions,
  children,
  className,
  testId,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <section
      className={cx('flex flex-col gap-3 rounded-xl border border-line bg-surface p-5', className)}
      data-testid={testId}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 font-heading text-[16px] font-semibold">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** Red „labela: vrijednost“ u sekciji podataka. */
export function Dl({ items }: { items: { label: string; value: ReactNode; testId?: string }[] }) {
  return (
    <dl className="m-0 grid grid-cols-[minmax(110px,max-content)_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
      {items.map((i) => (
        <div key={i.label} className="contents">
          <dt className="text-muted">{i.label}</dt>
          <dd className="m-0 min-w-0 break-words" data-testid={i.testId}>
            {i.value === null || i.value === undefined || i.value === '' ? '—' : i.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Jasna oznaka da je operater u oblasti platforme (ne u panelu firme). */
export function PlatformBadge() {
  const { t } = useTranslation();
  return (
    <span
      className="flex-none rounded-sm bg-chip px-2 py-0.5 text-[10px] font-semibold tracking-[.06em] text-accent uppercase"
      data-testid="platform-badge"
    >
      {t('panel:platform.badge')}
    </span>
  );
}

/** Rezervni kodovi (prikazuju se jednom): kopiranje + potvrda „Sačuvao sam kodove“. */
export function BackupCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <span className="text-sm font-semibold">{t('panel:platform.backupCodes.title')}</span>
        <span className="text-[13px] leading-normal text-text2">
          {t('panel:platform.backupCodes.intro')}
        </span>
      </div>
      <ul
        className="m-0 grid list-none grid-cols-2 gap-2 rounded-xl border border-line bg-bg2 p-4 font-mono text-sm"
        data-testid="platform-backup-codes"
      >
        {codes.map((c) => (
          <li key={c} className="select-all" data-testid="platform-backup-code">
            {c}
          </li>
        ))}
      </ul>
      <Button
        icon={<Copy size={15} />}
        onClick={() => void copyText(codes.join('\n')).then(setCopied)}
      >
        {copied ? t('panel:platform.login.copied') : t('panel:platform.backupCodes.copy')}
      </Button>
      <Button
        icon={<DownloadSimple size={15} />}
        data-testid="platform-backup-codes-download"
        onClick={() =>
          downloadBackupCodesPdf(
            codes,
            t('panel:platform.backupCodes.title'),
            t('panel:platform.backupCodes.intro'),
          )
        }
      >
        {t('panel:platform.backupCodes.download')}
      </Button>
      <Button variant="primary" onClick={onDone} data-testid="platform-backup-codes-done">
        {t('panel:platform.backupCodes.done')}
      </Button>
    </div>
  );
}

/** QR kod i tajna za aplikaciju za autentifikaciju (prvo uključivanje i promjena aplikacije za 2FA). */
export function TotpEnrollment({ enrollment }: { enrollment: PlatformTotpEnrollment }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <div
      className="flex flex-col gap-3 rounded-xl border border-line bg-bg2 p-4"
      data-testid="platform-enroll"
    >
      <span className="text-sm font-semibold">{t('panel:platform.login.enrollTitle')}</span>
      <span className="text-[13px] leading-normal text-text2">
        {t('panel:platform.login.enrollIntro')}
      </span>
      <div className="flex flex-wrap items-center gap-4">
        <img
          src={enrollment.qrDataUrl}
          alt={t('panel:platform.login.qrAlt')}
          width={168}
          height={168}
          className="rounded-lg border border-line bg-white p-2"
          data-testid="platform-enroll-qr"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="text-xs text-muted">{t('panel:platform.login.secret')}</span>
          <code
            className="rounded-md border border-line bg-surface px-2 py-1.5 font-mono text-[13px] break-all select-all"
            data-testid="platform-enroll-secret"
          >
            {enrollment.secret}
          </code>
          <Button
            size="sm"
            variant="ghost"
            className="self-start"
            icon={<Copy size={14} />}
            onClick={() => void copyText(enrollment.secret).then(setCopied)}
          >
            {copied ? t('panel:platform.login.copied') : t('panel:platform.login.copySecret')}
          </Button>
        </div>
      </div>
    </div>
  );
}
